/**
 * The registration form's game-side additions.
 *
 * Janeks asked for a password confirmation field, an eye toggle, a required
 * "accept Terms and Privacy" checkbox, and the "It takes less than a minute"
 * line gone.
 *
 * ─── Why this is injected instead of edited in ──────────────────────────────
 * `frontend/src/core/auth/register-screen.ts` is TEMPLATE-owned. CLAUDE.md
 * forbids editing `core/**` in a game repo and `.claude/hooks/
 * validate-core-edit.sh` enforces it by exiting 2 on any Edit or Write to that
 * path — this is not a preference that can be argued with at the call site.
 *
 * So the form is enhanced from outside, the same way `auth-chrome.ts` adds the
 * brand and the back arrow. The added fields are built with core's own classes
 * so they are indistinguishable from the ones core rendered.
 *
 * ─── What that costs, stated plainly ────────────────────────────────────────
 * This is DOM surgery on markup this repo does not own. It reads core's field
 * NAMES (`password`) and its `#register-form` / `#register-error` ids, none of
 * which carry a stability contract. Every lookup is therefore optional and the
 * whole enhancement no-ops if the shape it expects is not there — a register
 * screen that silently loses a confirmation field is far better than one that
 * throws on mount and renders nothing. `auth-form.test.ts` builds core's exact
 * markup and asserts against it, so an upstream change that breaks this fails
 * there rather than in front of a user.
 *
 * ─── Terms acceptance is CLIENT-SIDE ONLY ───────────────────────────────────
 * The checkbox gates the submit button in this browser and nothing more.
 * `POST /auth/register` neither accepts nor records it, and adding that would
 * be a change to core's DTO, core's controller and the Prisma schema. If the
 * acceptance needs to hold up legally — a timestamp, a document version, a
 * row — that is a backend task and it is NOT done here.
 */
import { legalPath } from './legal-paths';

/** Core's own input classes, copied so an injected field is indistinguishable. */
const INPUT_CLASS = 'w-full px-3 py-2.5 bg-surface text-text border border-border rounded'
  + ' outline-none transition-colors duration-[120ms] focus:border-gold focus:shadow-glow';
const LABEL_CLASS = 'block mb-1.5 text-[13px] text-text-dim';

const EYE = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"'
  + ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
const EYE_OFF = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"'
  + ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9'
  + ' 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1'
  + ' 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

/**
 * Enhances whichever auth form is in `root`. Returns a teardown.
 *
 * A no-op on the five screens that are not registration — they are found by
 * the absence of `#register-form`, not by inspecting the route, so this cannot
 * disagree with what is actually on screen.
 */
export function enhanceAuthForm(root: HTMLElement): () => void {
  const form = root.querySelector<HTMLFormElement>('#register-form');
  if (!form) return () => {};

  const cleanups: Array<() => void> = [];
  const errEl = root.querySelector<HTMLElement>('#register-error');
  const password = form.querySelector<HTMLInputElement>('input[name="password"]');
  const passwordLabel = password?.closest('label');

  // ── the subtitle goes, and the title grows into the space ─────────────────
  // Janeks: *"remove 'it takes a minute', slightly increase 'create an
  // account'."* The heading's new size is CSS (`.lp-auth-solo-title`); only
  // the removal has to happen here, because the text is core's.
  const title = form.querySelector('h1');
  const subtitle = title?.nextElementSibling;
  if (subtitle?.tagName === 'P') subtitle.remove();
  title?.classList.add('lp-auth-solo-title');

  // ── an eye on the password, and a confirmation beside it ──────────────────
  let confirmInput: HTMLInputElement | null = null;
  if (password && passwordLabel) {
    cleanups.push(addEye(password));

    const confirmLabel = document.createElement('label');
    confirmLabel.className = 'block mb-3.5';
    confirmLabel.innerHTML = `<span class="${LABEL_CLASS}">Repeat password</span>`;
    confirmInput = document.createElement('input');
    confirmInput.type = 'password';
    confirmInput.name = 'passwordConfirm';
    confirmInput.autocomplete = 'new-password';
    confirmInput.required = true;
    confirmInput.className = INPUT_CLASS;
    // Core prefills the password in DEV so the dev account can be created in
    // one click; a confirmation that did not match would undo that.
    confirmInput.value = password.value;
    confirmLabel.appendChild(confirmInput);
    passwordLabel.after(confirmLabel);
    cleanups.push(addEye(confirmInput));
    cleanups.push(() => confirmLabel.remove());
  }

  // ── terms and privacy ─────────────────────────────────────────────────────
  // Janeks: *"not a label, only checkbox is interactable."* So this is a DIV
  // and a SPAN, deliberately not a `<label>` — wrapping the text in a label
  // would make the whole sentence a toggle, and the sentence contains two
  // links that would then be impossible to click without also ticking the box.
  const terms = document.createElement('div');
  terms.className = 'lp-auth-terms';
  terms.innerHTML = `
    <input type="checkbox" id="lp-accept" name="acceptTerms" />
    <span>
      I accept the <a href="${legalPath('terms-of-service')}" target="_blank" rel="noopener">Terms and Conditions</a>
      and the <a href="${legalPath('privacy-policy')}" target="_blank" rel="noopener">Privacy Policy</a>
    </span>`;
  const accept = terms.querySelector<HTMLInputElement>('#lp-accept')!;
  // Before the submit button rather than at the end of the form: the last
  // thing above the button is the thing you are agreeing to by pressing it.
  const submit = form.querySelector('#register-btn');
  if (submit) submit.before(terms); else form.append(terms);
  cleanups.push(() => terms.remove());

  // ── validation, ahead of core's own submit handler ────────────────────────
  // CAPTURE phase. Core adds its handler with `form.addEventListener('submit',
  // handler)` — bubble — and a capture listener on the same element always
  // runs first, whatever order the two were registered in. That is what lets
  // this refuse a submit core would otherwise send.
  const onSubmit = (e: Event): void => {
    const fail = (message: string, focus?: HTMLElement): void => {
      e.preventDefault();
      // `stopImmediatePropagation`, not `stopPropagation`: core's listener is
      // on this same element, and only the immediate form stops a listener
      // attached to the very node the event is at.
      e.stopImmediatePropagation();
      if (errEl) {
        errEl.textContent = message;
        errEl.hidden = false;
      }
      focus?.focus();
    };
    if (confirmInput && password && confirmInput.value !== password.value) {
      fail('Those passwords do not match.', confirmInput);
      return;
    }
    if (!accept.checked) {
      fail('Please accept the Terms and Conditions and the Privacy Policy.', accept);
    }
  };
  form.addEventListener('submit', onSubmit, true);
  cleanups.push(() => form.removeEventListener('submit', onSubmit, true));

  return () => { for (const fn of cleanups) fn(); };
}

/**
 * Puts a show/hide toggle inside a password field.
 *
 * Core's login screen ships one and its register screen does not, so this
 * builds the same control rather than a second design of it — including the
 * `tabindex="-1"`, which keeps a decorative button out of the tab order
 * between the field and the submit.
 */
function addEye(input: HTMLInputElement): () => void {
  const wrap = document.createElement('div');
  wrap.className = 'relative';
  input.after(wrap);
  wrap.appendChild(input);
  input.classList.add('pr-10');

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.tabIndex = -1;
  btn.className = 'absolute right-2.5 top-1/2 -translate-y-1/2 bg-transparent border-0'
    + ' text-text-dim cursor-pointer p-0 leading-none hover:text-text';
  btn.innerHTML = EYE;
  btn.setAttribute('aria-label', 'Show password');
  const toggle = (): void => {
    const shown = input.type === 'text';
    input.type = shown ? 'password' : 'text';
    btn.innerHTML = shown ? EYE : EYE_OFF;
    btn.setAttribute('aria-label', shown ? 'Show password' : 'Hide password');
  };
  btn.addEventListener('click', toggle);
  wrap.appendChild(btn);

  return () => {
    btn.removeEventListener('click', toggle);
    // Put the input back where it was, so a teardown leaves core's DOM as
    // core built it.
    wrap.before(input);
    input.classList.remove('pr-10');
    wrap.remove();
  };
}
